import { useState, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Callout, Spinner, Button, Flex, TextField, Select } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { ChevronsDown, ChevronsUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import CreateAccessGroup from './elements/CreateAccessGroup';
import { AccessGroup } from './accessGroupsv2';
import { QUERY_CLIENT_ACCESSGROUPSV2 } from './query/accessGroupsV2';
import { UPSERT_USER_TO_ACCESS_GROUP } from './query/upsertUserV2';
import { REMOVE_USERV2_FROM_ACCESS_GROUP } from './query/removeUser';
import { UPSERT_ACCESS_GROUP_V2 } from './query/upsertAccessGroupV2';
import { DELETE_ACCESS_GROUP_V2 } from './query/deleteAccessGroupV2';
import { ADD_REMOVE_RESOURCE } from './query/addRemoveGroupResource';
import type { ClientAccessGroupsQuery } from './accessGroupsv2/elements/types/clientAccessGroupsQuery';

type SortField = 'name' | 'members' | 'resources';
type SortOrder = 'asc' | 'desc';

const AccessGroupsV2 = () => {
  const { t } = useTranslation();
  const {
    data: clientAccessGroups,
    refetch,
    loading: groupsLoading,
  } = useQuery<ClientAccessGroupsQuery>(QUERY_CLIENT_ACCESSGROUPSV2);

  const [addUserToGroup, { loading: addUserLoading }] = useMutation(UPSERT_USER_TO_ACCESS_GROUP);
  const [createAccessGroup, { loading: createGroupLoading }] = useMutation(UPSERT_ACCESS_GROUP_V2);
  const [deleteAccessGroup, { loading: deleteGroupLoading }] = useMutation(DELETE_ACCESS_GROUP_V2);
  const [removeUserGroup, { loading: removeUserGroupLoading }] = useMutation(REMOVE_USERV2_FROM_ACCESS_GROUP);
  const [addRemoveResourceGroup, { loading: addRemoveResourceLoading }] = useMutation(ADD_REMOVE_RESOURCE);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const allGroups = clientAccessGroups?.accessGroupsv2 || [];
  const allExpanded = useMemo(() => {
    if (allGroups.length === 0) return false;
    return allGroups.every((group) => openGroups[group.id]);
  }, [openGroups, allGroups]);

  const filteredAndSortedGroups = useMemo(() => {
    if (!allGroups) return [];

    let groups = [...allGroups];

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      groups = groups.filter((g) => {
        if ((g.name || '').toLowerCase().includes(lowerQuery)) return true;
        return g.members.some((m) => {
          const fName = (m.user?.firstName || '').toLowerCase();
          const lName = (m.user?.lastName || '').toLowerCase();
          const fullName = `${fName} ${lName}`;
          return fName.includes(lowerQuery) || lName.includes(lowerQuery) || fullName.includes(lowerQuery);
        });
      });
    }

    groups.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      } else if (sortField === 'members') {
        comparison = a.members.length - b.members.length;
      } else if (sortField === 'resources') {
        comparison = a.resources.length - b.resources.length;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return groups;
  }, [allGroups, searchQuery, sortField, sortOrder]);

  const toggleAll = () => {
    if (allExpanded) {
      setOpenGroups({});
    } else {
      const newOpenGroups: Record<string, boolean> = {};
      allGroups.forEach((group) => {
        newOpenGroups[group.id] = true;
      });
      setOpenGroups(newOpenGroups);
    }
  };

  const handleOpenChange = (id: string, isOpen: boolean) => {
    setOpenGroups((prev) => ({
      ...prev,
      [id]: isOpen,
    }));
  };

  const addRemoveResource = (agId: string, items: { resourceId: string; type: string }[]) => {
    return addRemoveResourceGroup({
      variables: {
        input: {
          accessGroupId: agId,
          items: items,
        },
      },
    }).then((response) => {
      if (response?.data?.addRemoveGroupResouceAccessv2?.isSuccess === true) {
        toast.success(t('settings.accessGroupv2.resourceUpdateSuccess'));
      } else {
        toast.error(t('settings.accessGroupv2.resourceUpdateFailed'));
      }
      refetch();
    });
  };

  const addUser = (agId: string, userId: string, roles: string[]) => {
    return addUserToGroup({
      variables: {
        upsertUserv2Input2: {
          accessGroupId: agId,
          userId,
          roles,
        },
      },
    }).then(() => {
      refetch();
    });
  };

  const deleteGroup = async (accessGroup: { id: string; name: string }) => {
    await deleteAccessGroup({
      variables: {
        input: {
          accessGroupId: accessGroup.id,
        },
      },
      update(cache) {
        cache.evict({
          id: cache.identify(accessGroup),
        });
      },
    });
  };

  const removeUserFromGroup = (accessGroupId: string, userId: string) => {
    return removeUserGroup({
      variables: {
        removeUserv2Input2: {
          accessGroupId: accessGroupId,
          userId: userId,
        },
      },
    })
      .then((response) => {
        const result = response?.data?.removeUserv2;
        if (result?.isSuccess) {
          refetch();
        } else {
          console.error('Failed to remove user:', result?.message || 'Unknown error');
          toast.error(result?.message || t('settings.accessGroupv2.removeUserFailed'));
        }
      })
      .catch((err) => {
        console.error('Mutation error:', err);
        toast.error(t('settings.accessGroupv2.unexpectedError'));
      });
  };

  const handleCreateGroupSubmit = (name: string) => {
    return createAccessGroup({
      variables: {
        input: {
          name: name,
        },
      },
    }).then(() => {
      refetch();
    });
  };

  return (
    <div className="border border-gray-200 rounded p-4">
      <div className="flex flex-row justify-between align-middle items-center mb-4">
        <h2 className="truncate">{t('settings.accessGroupv2.heading')}</h2>
        <div className="flex flex-row gap-2">
          <Flex gap="3" align="center">
            <TextField.Root
              placeholder={t('common.search', 'Search...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            >
              <TextField.Slot>
                <Search height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>
            <Select.Root
              value={`${sortField}-${sortOrder}`}
              onValueChange={(val) => {
                const parts = val.split('-');
                setSortField(parts[0] as SortField);
                setSortOrder(parts[1] as SortOrder);
              }}
            >
              <Select.Trigger />
              <Select.Content>
                <Select.Group>
                  <Select.Label>{t('common.sort', 'Sort by')}</Select.Label>
                  <Select.Item value="name-asc">{t('settings.sort.nameAsc', 'Name (A-Z)')}</Select.Item>
                  <Select.Item value="name-desc">{t('settings.sort.nameDesc', 'Name (Z-A)')}</Select.Item>
                  <Select.Item value="members-asc">
                    {t('settings.accessGroupv2.sort.membersAsc', 'Members (Lowest first)')}
                  </Select.Item>
                  <Select.Item value="members-desc">
                    {t('settings.accessGroupv2.sort.membersDesc', 'Members (Highest first)')}
                  </Select.Item>
                  <Select.Item value="resources-asc">
                    {t('settings.accessGroupv2.sort.resourcesAsc', 'Resources (Lowest first)')}
                  </Select.Item>
                  <Select.Item value="resources-desc">
                    {t('settings.accessGroupv2.sort.resourcesDesc', 'Resources (Highest first)')}
                  </Select.Item>
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </Flex>
          {allGroups.length > 0 && (
            <Button variant="outline" onClick={toggleAll} color="gray">
              {allExpanded ? (
                <>
                  <ChevronsUp size={16} className="mr-1" />
                  {t('settings.accessGroup.collapseAll', 'Collapse All')}
                </>
              ) : (
                <>
                  <ChevronsDown size={16} className="mr-1" />
                  {t('settings.accessGroup.expandAll', 'Expand All')}
                </>
              )}
            </Button>
          )}
          <CreateAccessGroup
            onSubmit={handleCreateGroupSubmit}
            loading={createGroupLoading}
            disable={!(clientAccessGroups?.member.isAdmin || clientAccessGroups?.member.isSuperAgAdmin)}
          />
        </div>
      </div>
      {clientAccessGroups?.accessGroupsv2?.length === 0 && (
        <Callout.Root color="blue">
          <Callout.Text className="mb-0">{t('settings.accessGroup.emptyState')}</Callout.Text>
        </Callout.Root>
      )}
      {clientAccessGroups &&
        filteredAndSortedGroups.map((group, index) => {
          return (
            <AccessGroup
              key={index}
              isOpen={!!openGroups[group.id]}
              onOpenChange={(isOpen) => handleOpenChange(group.id, isOpen)}
              accessGroup={{
                ...group,
                members: group.members.map((x) => ({
                  id: x.user.id,
                  firstName: x.user.firstName,
                  lastName: x.user.lastName,
                  roles: x.roles,
                  isSuperAgAdmin: x.isSuperAgAdmin,
                })),
              }}
              deleteGroup={deleteGroup}
              deleteGroupLoading={deleteGroupLoading}
              addUsers={addUser}
              addUsersLoading={addUserLoading}
              removeUser={removeUserFromGroup}
              removeUserLoading={removeUserGroupLoading}
              clientUsers={clientAccessGroups.member.client.members.nodes}
              allRoles={clientAccessGroups.allRoles}
              addRemoveResource={addRemoveResource}
              addRemoveResourceLoading={addRemoveResourceLoading}
              user={{
                accessGroupRoles: clientAccessGroups.member.accessGroupRoles ?? [],
                isAdmin: clientAccessGroups.member.isAdmin,
                isSuperAgAdmin: clientAccessGroups.member.isSuperAgAdmin,
              }}
            />
          );
        })}

      {groupsLoading && (
        <div className="flex justify-center">
          <Spinner size="3" role="status" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};
export default AccessGroupsV2;
